import React, {useState, useEffect, useRef, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  Switch,
  TouchableOpacity,
  ScrollView,
  Alert,
  Platform,
  TextInput,
  Modal,
  FlatList,
  Linking,
  ActivityIndicator,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {useAuth} from '../contexts/AuthContext';
import {
  ensureMutualAidCode,
  searchAccountByCode,
  addMutualAidPartner,
  removeMutualAidPartner,
  getPartnerAccount,
  toggleEnabledMutualAidPartner,
  toggleMutualAidNotificationPreference,
} from '../services/mutualAid';
import {civiliansApi} from '../services/civilians';
// Use navigation shim for iOS New Architecture compatibility
import {useNavigation} from '../navigation/navigationShim';
import {closuresApi} from '../services/api';
import {sendNotificationsToAccount, createOrUpdateNotificationChannel, requestNotificationPermissions} from '../services/notifications';
import {previewSound, stopPreview} from '../utils/soundPlayer';
// Document picker for selecting sound files
let DocumentPicker: any = null;
try {
  DocumentPicker = require('@react-native-documents/picker').default || require('@react-native-documents/picker');
} catch (error) {
  console.warn('Document picker not available:', error);
}
// File system for copying sound files
let RNFS: any = null;
try {
  RNFS = require('react-native-fs');
} catch (error) {
  console.warn('react-native-fs not available:', error);
}

const SETTINGS_KEYS = {
  NIGHT_MODE: 'settings_night_mode',
  SOUND_URI: 'settings_sound_uri',
  SOUND_NAME: 'settings_sound_name',
  SOUND_CLOSED_URI: 'settings_sound_closed_uri',
  SOUND_CLOSED_NAME: 'settings_sound_closed_name',
  SOUND_OPENED_URI: 'settings_sound_opened_uri',
  SOUND_OPENED_NAME: 'settings_sound_opened_name',
};

interface SettingsScreenProps {
  onSettingsChange?: (settings: {
    nightMode: boolean;
    soundUri?: string;
    soundName?: string;
  }) => void;
}

// List of available sounds bundled in res/raw
// These must match the actual files in android/app/src/main/res/raw/
const AVAILABLE_SOUNDS = [
  {filename: 'system_default', displayName: 'System Default'},
  {filename: 'a_new_road_is_closed', displayName: 'A New Road Is Closed'},
  {filename: 'arabic_style', displayName: 'Arabic Style'},
  {filename: 'boom_splash', displayName: 'Boom Splash'},
  {filename: 'braam', displayName: 'Braam'},
  {filename: 'bright', displayName: 'Bright'},
  {filename: 'chime1', displayName: 'Chime 1'},
  {filename: 'chime2', displayName: 'Chime 2'},
  {filename: 'closed_chime', displayName: 'Closed Chime'},
  {filename: 'denied', displayName: 'Denied'},
  {filename: 'ding_ding', displayName: 'Ding Ding'},
  {filename: 'happy1', displayName: 'Happy 1'},
  {filename: 'happy2', displayName: 'Happy 2'},
  {filename: 'hooray', displayName: 'Hooray'},
  {filename: 'regrettably_buzzer', displayName: 'Regrettably Buzzer'},
  {filename: 'regrettably_closed_female', displayName: 'Regrettably Closed'},
  {filename: 'road_closed', displayName: 'Road Closed'},
  {filename: 'road_now_open', displayName: 'Road Now Open'},
  {filename: 'road_open', displayName: 'Road Open'},
  {filename: 'road_open1', displayName: 'Road Open 1'},
  {filename: 'road_open2', displayName: 'Road Open 2'},
  {filename: 'siren_1', displayName: 'Siren 1'},
  {filename: 'siren_short', displayName: 'Siren Short'},
  {filename: 'ukulele', displayName: 'Ukulele'},
  {filename: 'wilhelm_full', displayName: 'Wilhelm Full'},
];


const SettingsScreen: React.FC<SettingsScreenProps> = ({onSettingsChange}) => {
  const insets = useSafeAreaInsets();
  const {account, refreshAccount, userProfile, user, refreshUserProfile} = useAuth();
  const navigation = useNavigation();
  const [nightMode, setNightMode] = useState(false);
  const [showSoundPicker, setShowSoundPicker] = useState(false);
  const [soundPickerType, setSoundPickerType] = useState<'closed' | 'opened' | 'general'>('general');
  const [soundUri, setSoundUri] = useState<string | undefined>(undefined);
  const [soundName, setSoundName] = useState<string>('Default');
  const [soundClosedUri, setSoundClosedUri] = useState<string | undefined>(undefined);
  const [soundClosedName, setSoundClosedName] = useState<string>('Default');
  const [soundOpenedUri, setSoundOpenedUri] = useState<string | undefined>(undefined);
  const [soundOpenedName, setSoundOpenedName] = useState<string>('Default');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewingSound, setPreviewingSound] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Mutual Aid Partners state
  const [mutualAidCode, setMutualAidCode] = useState<string>('');
  const [partnerSearchCode, setPartnerSearchCode] = useState<string>('');
  const [partners, setPartners] = useState<Array<{id: string; name: string; municipality: string}>>([]);
  const [loadingCode, setLoadingCode] = useState(false);
  const [searchingPartner, setSearchingPartner] = useState(false);
  
  // Civilian Followed Accounts state
  const [followedAccounts, setFollowedAccounts] = useState<Array<{id: string; name: string; municipality: string; email?: string; verified?: boolean}>>([]);
  const [loadingFollowedAccounts, setLoadingFollowedAccounts] = useState(false);
  
  // Open All Closures state
  const [openingAllClosures, setOpeningAllClosures] = useState(false);
  
  // On Duty state for municipality users
  const [onDuty, setOnDuty] = useState(true);
  const onDutyInitializedRef = useRef(false);

  // Web-only: track browser notification permission so we can show the right
  // button state in the Notification Sounds section. Native platforms manage
  // this via FCM/messaging, not the Notification API.
  const [webNotificationPermission, setWebNotificationPermission] = useState<
    'default' | 'granted' | 'denied' | 'unsupported'
  >('default');

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const NotificationApi = (globalThis as any)?.Notification as
      | (typeof Notification & {permission: NotificationPermission})
      | undefined;
    if (!NotificationApi) {
      setWebNotificationPermission('unsupported');
      return;
    }
    setWebNotificationPermission(NotificationApi.permission);
  }, []);

  const handleEnableWebNotifications = async () => {
    try {
      const granted = await requestNotificationPermissions();
      const NotificationApi = (globalThis as any)?.Notification as
        | (typeof Notification & {permission: NotificationPermission})
        | undefined;
      if (NotificationApi) {
        setWebNotificationPermission(NotificationApi.permission);
      } else {
        setWebNotificationPermission(granted ? 'granted' : 'denied');
      }
    } catch (err) {
      console.warn('Failed to request web notification permission:', err);
    }
  };

  useEffect(() => {
    loadSettings();
    loadMutualAidData();
  }, [account]);
  
  // Initialize onDuty from user profile ONLY on first mount
  // Using a ref prevents race conditions with state updates
  useEffect(() => {
    if (userProfile && userProfile.role !== 'civilian' && !onDutyInitializedRef.current) {
      // Default to true if not set (existing users)
      const initialValue = userProfile.onDuty !== false;
      console.log('📋 Initializing onDuty from profile:', initialValue);
      setOnDuty(initialValue);
      onDutyInitializedRef.current = true;
    }
  }, [userProfile]);

  // Load followed accounts for civilians
  useEffect(() => {
    if (userProfile?.role === 'civilian' && userProfile.followedAccounts) {
      loadFollowedAccounts();
    }
  }, [userProfile?.role, userProfile?.followedAccounts]);

  // Load mutual aid code and partners
  const loadMutualAidData = async () => {
    if (!account?.id) return;
    
    try {
      // Ensure code exists and load it
      const code = await ensureMutualAidCode(account.id);
      setMutualAidCode(code);
      
      // Load partner details
      if (account.mutualAidPartners && account.mutualAidPartners.length > 0) {
        const partnerDetails = await Promise.all(
          account.mutualAidPartners.map(partnerId => getPartnerAccount(partnerId))
        );
        setPartners(partnerDetails.filter(p => p !== null) as Array<{id: string; name: string; municipality: string}>);
      } else {
        setPartners([]);
      }
    } catch (error) {
      console.error('Error loading mutual aid data:', error);
    }
  };

  // Handle adding a partner
  const handleAddPartner = async () => {
    if (!account?.id) {
      Alert.alert('Error', 'Account not loaded');
      return;
    }
    
    const code = partnerSearchCode.trim().toUpperCase();
    if (!code) {
      Alert.alert('Error', 'Please enter a mutual aid code');
      return;
    }
    
    setSearchingPartner(true);
    try {
      const partnerAccount = await searchAccountByCode(code);
      
      if (!partnerAccount) {
        Alert.alert('Not Found', 'No account found with that code');
        setPartnerSearchCode('');
        return;
      }
      
      if (partnerAccount.id === account.id) {
        Alert.alert('Error', 'Cannot add your own account');
        setPartnerSearchCode('');
        return;
      }
      
      // Check if already added
      if (account.mutualAidPartners?.includes(partnerAccount.id)) {
        Alert.alert('Already Added', 'This partner is already in your list');
        setPartnerSearchCode('');
        return;
      }
      
      await addMutualAidPartner(account.id, partnerAccount.id);
      // Automatically enable the new partner
      await toggleEnabledMutualAidPartner(account.id, partnerAccount.id, true);
      Alert.alert('Success', `Added ${partnerAccount.name} as a mutual aid partner`);
      setPartnerSearchCode('');
      
      // Refresh account to get updated partners list
      await refreshAccount();
      await loadMutualAidData();
    } catch (error: any) {
      console.error('Error adding partner:', error);
      Alert.alert('Error', error.message || 'Failed to add partner');
    } finally {
      setSearchingPartner(false);
    }
  };

  // Handle toggling enabled state for a partner
  const handleTogglePartner = async (partnerId: string, enabled: boolean) => {
    if (!account?.id) {
      Alert.alert('Error', 'Account not loaded');
      return;
    }
    
    try {
      await toggleEnabledMutualAidPartner(account.id, partnerId, enabled);
      // Refresh account to get updated enabled list
      await refreshAccount();
    } catch (error: any) {
      console.error('Error toggling partner:', error);
      Alert.alert('Error', error.message || 'Failed to update partner');
    }
  };

  // Handle toggling notification preference for a mutual aid partner (user-level)
  const handleToggleMutualAidNotification = async (partnerId: string, enabled: boolean) => {
    if (!user?.uid) {
      Alert.alert('Error', 'User not loaded');
      return;
    }
    
    try {
      console.log('🔔 Toggling mutual aid notification preference:', {partnerId, enabled, userId: user.uid});
      await toggleMutualAidNotificationPreference(user.uid, partnerId, enabled);
      console.log('✅ Mutual aid notification preference updated');
      
      // Refresh user profile to get updated preferences
      if (refreshUserProfile) {
        await refreshUserProfile();
        console.log('✅ User profile refreshed');
      }
    } catch (error: any) {
      console.error('Error toggling mutual aid notification:', error);
      Alert.alert('Error', error.message || 'Failed to update notification preference');
    }
  };

  // Handle removing a partner
  const handleRemovePartner = async (partnerId: string) => {
    if (!account?.id) {
      Alert.alert('Error', 'Account not loaded');
      return;
    }

    const performRemovePartner = async () => {
      try {
        await removeMutualAidPartner(account.id, partnerId);
        Alert.alert('Success', 'Partner removed');

        // Web can show stale account-derived data briefly; update list immediately.
        if (Platform.OS === 'web') {
          setPartners(prev => prev.filter(p => p.id !== partnerId));
        }

        // Refresh account
        await refreshAccount();
        // On web, wait for the account-driven effect to reload partner data.
        if (Platform.OS !== 'web') {
          await loadMutualAidData();
        }
      } catch (error: any) {
        console.error('Error removing partner:', error);
        Alert.alert('Error', error.message || 'Failed to remove partner');
      }
    };

    // Web does not reliably support Alert button callbacks.
    if (Platform.OS === 'web') {
      const confirmFn = (globalThis as any)?.confirm as
        | ((message?: string) => boolean)
        | undefined;
      const confirmed = confirmFn
        ? confirmFn('Are you sure you want to remove this mutual aid partner?')
        : true;
      if (!confirmed) return;
      await performRemovePartner();
      return;
    }

    Alert.alert(
      'Remove Partner',
      'Are you sure you want to remove this mutual aid partner?',
      [
        {text: 'Cancel', style: 'cancel'},
        {
          text: 'Remove',
          style: 'destructive',
          onPress: performRemovePartner,
        },
      ],
    );
  };

  // Copy code to clipboard
  const copyCodeToClipboard = async () => {
    if (!mutualAidCode) return;
    
    try {
      if (Platform.OS === 'web') {
        const clipboardApi = (globalThis as any)?.navigator?.clipboard;
        const alertFn = (globalThis as any)?.alert as
          | ((message?: string) => void)
          | undefined;
        if (clipboardApi?.writeText) {
          await clipboardApi.writeText(mutualAidCode);
          if (alertFn) {
            alertFn('Mutual aid code copied to clipboard');
          } else {
            Alert.alert('Copied', 'Mutual aid code copied to clipboard');
          }
          return;
        }
      }

      const clipboardModule = require('@react-native-clipboard/clipboard');
      const Clipboard = clipboardModule?.default || clipboardModule;
      await Clipboard.setString(mutualAidCode);
      Alert.alert('Copied', 'Mutual aid code copied to clipboard');
    } catch (error) {
      // Fallback if clipboard not available
      if (Platform.OS === 'web') {
        const alertFn = (globalThis as any)?.alert as
          | ((message?: string) => void)
          | undefined;
        if (alertFn) {
          alertFn(`Code: ${mutualAidCode}`);
          return;
        }
      }
      Alert.alert('Code', mutualAidCode);
    }
  };

  // Load followed accounts for civilians
  const loadFollowedAccounts = async () => {
    if (!userProfile?.followedAccounts || !user) return;
    
    setLoadingFollowedAccounts(true);
    try {
      const accountDetails = await Promise.all(
        userProfile.followedAccounts.map(accountId => civiliansApi.getFollowedAccount(accountId))
      );
      const validAccounts = accountDetails.filter(a => a !== null) as Array<{id: string; name: string; municipality: string; email?: string}>;
      setFollowedAccounts(validAccounts);
    } catch (error) {
      console.error('Error loading followed accounts:', error);
      Alert.alert('Error', 'Failed to load followed accounts');
    } finally {
      setLoadingFollowedAccounts(false);
    }
  };

  // Toggle notification preference for a followed account
  const handleToggleNotification = async (accountId: string, enabled: boolean) => {
    if (!user?.uid) return;
    
    try {
      console.log('🔔 Toggling notification preference:', {accountId, enabled, userId: user.uid});
      await civiliansApi.toggleNotificationPreference(user.uid, accountId, enabled);
      console.log('✅ Notification preference updated in Firestore');
      
      // Wait a moment for Firestore to propagate
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Refresh user profile to get updated preferences
      if (refreshUserProfile) {
        await refreshUserProfile();
        console.log('✅ User profile refreshed');
      }
      
      // Also reload followed accounts to update the UI
      await loadFollowedAccounts();
      console.log('✅ Followed accounts reloaded');
    } catch (error: any) {
      console.error('❌ Error toggling notification preference:', error);
      console.error('Error details:', error.message, error.code);
      Alert.alert('Error', error.message || 'Failed to update notification preference');
    }
  };

  // Unfollow an account
  const handleUnfollow = async (accountId: string) => {
    if (!user?.uid) return;

    const performUnfollow = async () => {
      try {
        await civiliansApi.unfollowAccount(user.uid, accountId);
        // Refresh user profile to update AuthContext
        await refreshUserProfile();
        await loadFollowedAccounts();
        Alert.alert('Success', 'Stopped following municipality');
      } catch (error: any) {
        Alert.alert('Error', error.message || 'Failed to unfollow account');
      }
    };

    if (Platform.OS === 'web') {
      const confirmFn = (globalThis as any)?.confirm as
        | ((message?: string) => boolean)
        | undefined;
      const confirmed = confirmFn
        ? confirmFn('Are you sure you want to stop following this municipality? You will no longer see their road closures.')
        : true;
      if (!confirmed) return;
      await performUnfollow();
      return;
    }

    Alert.alert(
      'Unfollow Municipality',
      'Are you sure you want to stop following this municipality? You will no longer see their road closures.',
      [
        {text: 'Cancel', style: 'cancel'},
        {
          text: 'Unfollow',
          style: 'destructive',
          onPress: performUnfollow,
        },
      ],
    );
  };

  const loadSettings = async () => {
    try {
      const [
        nightModeValue,
        storedSoundUri,
        storedSoundName,
        storedSoundClosedUri,
        storedSoundClosedName,
        storedSoundOpenedUri,
        storedSoundOpenedName,
      ] = await Promise.all([
        AsyncStorage.getItem(SETTINGS_KEYS.NIGHT_MODE),
        AsyncStorage.getItem(SETTINGS_KEYS.SOUND_URI),
        AsyncStorage.getItem(SETTINGS_KEYS.SOUND_NAME),
        AsyncStorage.getItem(SETTINGS_KEYS.SOUND_CLOSED_URI),
        AsyncStorage.getItem(SETTINGS_KEYS.SOUND_CLOSED_NAME),
        AsyncStorage.getItem(SETTINGS_KEYS.SOUND_OPENED_URI),
        AsyncStorage.getItem(SETTINGS_KEYS.SOUND_OPENED_NAME),
      ]);

      // Explicitly default to false for night mode if not set or if value is invalid
      const nightMode = nightModeValue === 'true';
      setNightMode(nightMode);
      
      // If night mode was somehow stuck on, log it and reset it
      if (nightMode && nightModeValue !== null) {
        console.log('🌙 Night mode found in storage:', nightModeValue);
      }
      
      setSoundUri(storedSoundUri || undefined);
      setSoundName(storedSoundName || 'Default');
      
      // Set default sounds on first install (when values are null)
      let finalSoundClosedUri = storedSoundClosedUri;
      let finalSoundClosedName = storedSoundClosedName;
      let finalSoundOpenedUri = storedSoundOpenedUri;
      let finalSoundOpenedName = storedSoundOpenedName;
      
      // Initialize default "Road Closed" sound if not set
      const needsClosedDefault = !storedSoundClosedUri && !storedSoundClosedName;
      if (needsClosedDefault) {
        finalSoundClosedUri = 'system_default';
        finalSoundClosedName = 'system_default';
        await Promise.all([
          AsyncStorage.setItem(SETTINGS_KEYS.SOUND_CLOSED_URI, finalSoundClosedUri),
          AsyncStorage.setItem(SETTINGS_KEYS.SOUND_CLOSED_NAME, finalSoundClosedName),
        ]);
        console.log('🔊 Set default Road Closed sound: System Default');
      }
      
      // Initialize default "Road Opened" sound if not set
      const needsOpenedDefault = !storedSoundOpenedUri && !storedSoundOpenedName;
      if (needsOpenedDefault) {
        finalSoundOpenedUri = 'system_default';
        finalSoundOpenedName = 'system_default';
        await Promise.all([
          AsyncStorage.setItem(SETTINGS_KEYS.SOUND_OPENED_URI, finalSoundOpenedUri),
          AsyncStorage.setItem(SETTINGS_KEYS.SOUND_OPENED_NAME, finalSoundOpenedName),
        ]);
        console.log('🔊 Set default Road Opened sound: System Default');
      }

      // Sync defaults to Firestore so the Cloud Function uses system_default
      if ((needsClosedDefault || needsOpenedDefault) && user?.uid) {
        try {
          const firestore = require('@react-native-firebase/firestore').default;
          const updateData: any = {
            updatedAt: firestore.FieldValue.serverTimestamp(),
          };
          if (needsClosedDefault) {
            updateData.closedSoundFilename = 'system_default';
          }
          if (needsOpenedDefault) {
            updateData.openedSoundFilename = 'system_default';
          }
          await firestore().collection('users').doc(user.uid).update(updateData);
          console.log('🔊 Synced default sound preferences to Firestore');
        } catch (firestoreError) {
          console.error('❌ Error syncing default sounds to Firestore:', firestoreError);
        }
      }
      
      setSoundClosedUri(finalSoundClosedUri || undefined);
      setSoundClosedName(finalSoundClosedName || 'Default');
      setSoundOpenedUri(finalSoundOpenedUri || undefined);
      setSoundOpenedName(finalSoundOpenedName || 'Default');
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async (
    newNightMode?: boolean,
    newSoundUri?: string,
    newSoundName?: string,
    newSoundClosedUri?: string,
    newSoundClosedName?: string,
    newSoundOpenedUri?: string,
    newSoundOpenedName?: string,
  ) => {
    try {
      const updatedNightMode = newNightMode !== undefined ? newNightMode : nightMode;
      const updatedSoundUri = newSoundUri !== undefined ? newSoundUri : soundUri;
      const updatedSoundName = newSoundName !== undefined ? newSoundName : soundName;
      const updatedSoundClosedUri = newSoundClosedUri !== undefined ? newSoundClosedUri : soundClosedUri;
      const updatedSoundClosedName = newSoundClosedName !== undefined ? newSoundClosedName : soundClosedName;
      const updatedSoundOpenedUri = newSoundOpenedUri !== undefined ? newSoundOpenedUri : soundOpenedUri;
      const updatedSoundOpenedName = newSoundOpenedName !== undefined ? newSoundOpenedName : soundOpenedName;

      await Promise.all([
        AsyncStorage.setItem(SETTINGS_KEYS.NIGHT_MODE, String(updatedNightMode)),
        updatedSoundUri
          ? AsyncStorage.setItem(SETTINGS_KEYS.SOUND_URI, updatedSoundUri)
          : AsyncStorage.removeItem(SETTINGS_KEYS.SOUND_URI),
        updatedSoundName
          ? AsyncStorage.setItem(SETTINGS_KEYS.SOUND_NAME, updatedSoundName)
          : AsyncStorage.removeItem(SETTINGS_KEYS.SOUND_NAME),
        updatedSoundClosedUri
          ? AsyncStorage.setItem(SETTINGS_KEYS.SOUND_CLOSED_URI, updatedSoundClosedUri)
          : AsyncStorage.removeItem(SETTINGS_KEYS.SOUND_CLOSED_URI),
        updatedSoundClosedName
          ? AsyncStorage.setItem(SETTINGS_KEYS.SOUND_CLOSED_NAME, updatedSoundClosedName)
          : AsyncStorage.removeItem(SETTINGS_KEYS.SOUND_CLOSED_NAME),
        updatedSoundOpenedUri
          ? AsyncStorage.setItem(SETTINGS_KEYS.SOUND_OPENED_URI, updatedSoundOpenedUri)
          : AsyncStorage.removeItem(SETTINGS_KEYS.SOUND_OPENED_URI),
        updatedSoundOpenedName
          ? AsyncStorage.setItem(SETTINGS_KEYS.SOUND_OPENED_NAME, updatedSoundOpenedName)
          : AsyncStorage.removeItem(SETTINGS_KEYS.SOUND_OPENED_NAME),
      ]);

      console.log('💾 Settings saved:', {
        nightMode: updatedNightMode,
        soundClosedUri: updatedSoundClosedUri,
        soundClosedName: updatedSoundClosedName,
        soundOpenedUri: updatedSoundOpenedUri,
        soundOpenedName: updatedSoundOpenedName,
      });

      if (newNightMode !== undefined) setNightMode(updatedNightMode);
      if (newSoundUri !== undefined) setSoundUri(updatedSoundUri);
      if (newSoundName !== undefined) setSoundName(updatedSoundName);
      if (newSoundClosedUri !== undefined) setSoundClosedUri(updatedSoundClosedUri);
      if (newSoundClosedName !== undefined) setSoundClosedName(updatedSoundClosedName);
      if (newSoundOpenedUri !== undefined) setSoundOpenedUri(updatedSoundOpenedUri);
      if (newSoundOpenedName !== undefined) setSoundOpenedName(updatedSoundOpenedName);

      // Notify parent component of settings change
      if (onSettingsChange) {
        onSettingsChange({
          nightMode: updatedNightMode,
          soundUri: updatedSoundUri,
          soundName: updatedSoundName,
        });
      }

      // Recreate notification channels when sound file selection changes
      if (Platform.OS === 'android' && (newSoundClosedUri !== undefined || newSoundOpenedUri !== undefined)) {
        console.log('🔔 Sound selection changed, recreating notification channels...');
        await createOrUpdateNotificationChannel(updatedSoundClosedUri, updatedSoundOpenedUri);
        console.log('🔔 Notification channels recreated with new sound');
      }
      
      // CRITICAL: Save sound preferences to Firestore so Cloud Function can read them
      // This is required for background notifications to use the correct sound
      // The Cloud Function reads each user's preferences to send to the correct channel
      if (user?.uid && (newSoundClosedUri !== undefined || newSoundOpenedUri !== undefined)) {
        console.log('🔔 Saving sound preferences to Firestore...');
        const firestore = require('@react-native-firebase/firestore').default;
        const updateData: any = {
          updatedAt: firestore.FieldValue.serverTimestamp(),
        };
        
        // Only save bundled sounds (not content:// URIs) - these are the only ones that work
        if (newSoundClosedUri !== undefined) {
          const closedFilename = updatedSoundClosedUri && !updatedSoundClosedUri.startsWith('content://') 
            ? updatedSoundClosedUri 
            : 'road_closed';
          updateData.closedSoundFilename = closedFilename;
          console.log('🔊 Saving closed sound to Firestore:', closedFilename);
        }
        
        if (newSoundOpenedUri !== undefined) {
          const openedFilename = updatedSoundOpenedUri && !updatedSoundOpenedUri.startsWith('content://') 
            ? updatedSoundOpenedUri 
            : 'road_open';
          updateData.openedSoundFilename = openedFilename;
          console.log('🔊 Saving opened sound to Firestore:', openedFilename);
        }
        
        try {
          await firestore().collection('users').doc(user.uid).update(updateData);
          console.log('✅ Sound preferences saved to Firestore');
          
          // Refresh user profile to get updated values
          if (refreshUserProfile) {
            await refreshUserProfile();
          }
        } catch (firestoreError) {
          console.error('❌ Error saving sound preferences to Firestore:', firestoreError);
          // Don't fail the whole save - local settings still work for foreground
        }
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      Alert.alert('Error', 'Failed to save settings');
    }
  };

  const handleNightModeToggle = (value: boolean) => {
    console.log('🌙 Toggling night mode to:', value);
    saveSettings(value);
  };

  // Handle On Duty toggle for municipality users
  const handleOnDutyToggle = async (value: boolean) => {
    if (!user?.uid) return;
    
    // Update UI immediately
    setOnDuty(value);
    console.log(`📋 Toggling duty status to: ${value ? 'On Duty' : 'Off Duty'}`);
    
    try {
      const firestore = require('@react-native-firebase/firestore').default;
      await firestore().collection('users').doc(user.uid).update({
        onDuty: value,
        updatedAt: firestore.FieldValue.serverTimestamp(),
      });
      console.log(`📋 Duty status saved to Firestore: ${value ? 'On Duty' : 'Off Duty'}`);
      
      // Refresh the user profile in context so it has the updated value
      // This ensures the value persists when returning to settings
      await refreshUserProfile();
      console.log(`📋 User profile refreshed with new duty status`);
    } catch (error) {
      console.error('Error updating duty status:', error);
      // Revert on error
      setOnDuty(!value);
      Alert.alert('Error', 'Failed to update duty status');
    }
  };

  const handleSelectSound = async () => {
    if (!DocumentPicker) {
      Alert.alert(
        'Not Available',
        'Sound file picker requires the app to be rebuilt. Please rebuild the app after installing react-native-document-picker.',
      );
      return;
    }

    try {
      // Check if pick method exists
      if (!DocumentPicker.pick) {
        Alert.alert(
          'Not Available',
          'Document picker is not properly linked. Please rebuild the app.',
        );
        return;
      }

      const result = await DocumentPicker.pick({
        type: DocumentPicker.types?.audio || ['audio/*'],
        copyTo: 'cachesDirectory',
      });

      if (result && result.length > 0) {
        const file = result[0];
        const fileName = file.name || 'Custom Sound';
        let soundUri = file.uri;
        
        // CRITICAL: Check file format - Android notifications require specific formats
        const fileExtension = fileName.includes('.') 
          ? fileName.substring(fileName.lastIndexOf('.')).toLowerCase() 
          : '';
        const supportedFormats = ['.mp3', '.wav', '.ogg', '.m4a'];
        const isSupportedFormat = supportedFormats.includes(fileExtension);
        
        if (!isSupportedFormat && fileExtension) {
          console.warn('⚠️ File format may not be supported:', fileExtension);
          console.warn('   Android notifications typically require: .mp3, .wav, .ogg, or .m4a');
          Alert.alert(
            'Format Warning',
            `The file format "${fileExtension}" may not be supported. Android notifications work best with .mp3, .wav, .ogg, or .m4a files. The sound may not play correctly.`,
          );
        }
        
        // CRITICAL: Notifee on Android ONLY supports sounds in res/raw directory
        // MediaStore URIs and file selection won't work!
        // For custom sounds:
        // 1. Manually copy sound files to android/app/src/main/res/raw/
        // 2. Reference by filename WITHOUT extension (e.g., "alert" for "alert.mp3")
        if (soundUri && Platform.OS === 'android') {
          if (soundUri.startsWith('content://') || soundUri.startsWith('file://')) {
            // Document picker or MediaStore URI - Notifee doesn't support this!
            Alert.alert(
              'Custom Sound Limitation',
              `Notifee on Android only supports sounds bundled in the app.\n\n` +
              `To use custom sounds:\n` +
              `1. Copy your sound file to: android/app/src/main/res/raw/\n` +
              `2. Rebuild the app\n` +
              `3. In Settings, enter the filename WITHOUT extension\n` +
              `   (e.g., if file is "alert.mp3", enter "alert")\n\n` +
              `The selected file will be saved, but won't play until you bundle it in res/raw.`,
            );
            // Still save the filename for reference, but warn user
            const fileNameWithoutExt = fileName.includes('.') 
              ? fileName.substring(0, fileName.lastIndexOf('.'))
              : fileName;
            console.warn('⚠️ File selection not supported for notifications');
            console.warn('   User must manually copy to res/raw and use filename:', fileNameWithoutExt);
          }
        }
        
        // For Android, if it's not a URI, assume it's already a res/raw filename
        // For iOS or other cases, use the URI as-is
        const soundToSave = (Platform.OS === 'android' && 
                             !soundUri.startsWith('content://') && 
                             !soundUri.startsWith('file://'))
          ? soundUri // Already a filename for res/raw
          : soundUri; // Keep URI for now (iOS or future support)
        
        saveSettings(undefined, soundToSave, fileName);
        
        if (Platform.OS === 'android' && (soundUri.startsWith('content://') || soundUri.startsWith('file://'))) {
          // Don't show success if it won't work
          console.warn('Sound saved but will not work - user must bundle in res/raw');
        } else {
        Alert.alert('Success', `Sound selected: ${fileName}`);
        }
      }
    } catch (error: any) {
      if (DocumentPicker?.isCancel && DocumentPicker.isCancel(error)) {
        // User cancelled
        return;
      }
      console.error('Error selecting sound:', error);
      Alert.alert(
        'Error',
        'Failed to select sound file. The document picker may need to be linked. Please rebuild the app.',
      );
    }
  };

  const handleResetSound = () => {
    const performReset = () => {
      saveSettings(undefined, undefined, 'Default');
      Alert.alert('Success', 'Sound reset to default');
    };

    if (Platform.OS === 'web') {
      const confirmFn = (globalThis as any)?.confirm as
        | ((message?: string) => boolean)
        | undefined;
      const confirmed = confirmFn
        ? confirmFn('Reset to default notification sound?')
        : true;
      if (!confirmed) return;
      performReset();
      return;
    }

    Alert.alert(
      'Reset Sound',
      'Reset to default notification sound?',
      [
        {text: 'Cancel', style: 'cancel'},
        {
          text: 'Reset',
          onPress: performReset,
        },
      ],
    );
  };

  const handleOpenAllClosures = () => {
    const performOpenAllClosures = async () => {
      if (!account?.id) {
        Alert.alert('Error', 'Account not available');
        return;
      }

      setOpeningAllClosures(true);
      try {
        // Open all closures (actor is attributed in the closure audit log)
        const count = await closuresApi.openAllClosures(account.id, {
          email: userProfile?.email || user?.email || '',
          name: userProfile?.name || '',
          id: userProfile?.id || user?.uid || '',
        });
        
        if (count === 0) {
          Alert.alert('Info', 'No closed closures to open');
          setOpeningAllClosures(false);
          return;
        }

        // Send notification
        await sendNotificationsToAccount(account.id, {
          title: '✅ Road Reopened',
          body: 'All roads are now open',
          data: {
            type: 'closure_removed',
          },
        });

        Alert.alert('Success', `Opened ${count} closure${count !== 1 ? 's' : ''}`);
      } catch (error: any) {
        console.error('Error opening all closures:', error);
        Alert.alert('Error', error.message || 'Failed to open all closures');
      } finally {
        setOpeningAllClosures(false);
      }
    };

    if (Platform.OS === 'web') {
      const confirmFn = (globalThis as any)?.confirm as
        | ((message?: string) => boolean)
        | undefined;
      const confirmed = confirmFn
        ? confirmFn('Are you sure you want to open all closures? This will mark all roads as open and send a notification.')
        : true;
      if (!confirmed) return;
      void performOpenAllClosures();
      return;
    }

    Alert.alert(
      'Open All Closures',
      'Are you sure you want to open all closures? This will mark all roads as open and send a notification.',
      [
        {text: 'Cancel', style: 'cancel'},
        {
          text: 'Open All',
          style: 'destructive',
          onPress: performOpenAllClosures,
        },
      ],
    );
  };

  // Get bundled sounds for display
  const getFilteredSounds = () => {
    if (!searchQuery.trim()) {
      return AVAILABLE_SOUNDS;
    }
    const query = searchQuery.toLowerCase();
    return AVAILABLE_SOUNDS.filter(sound =>
      sound.displayName.toLowerCase().includes(query)
    );
  };

  const handleSoundSelection = (soundIdentifier: string, displayName: string) => {
    // Stop any preview before selecting
    handleStopPreview();
    if (soundPickerType === 'closed') {
      saveSettings(undefined, undefined, undefined, soundIdentifier, displayName);
    } else if (soundPickerType === 'opened') {
      saveSettings(undefined, undefined, undefined, undefined, undefined, soundIdentifier, displayName);
    } else {
      saveSettings(undefined, soundIdentifier, displayName);
    }
    setShowSoundPicker(false);
    setSearchQuery('');
    const soundType = soundPickerType === 'closed' ? 'Road Closed' : soundPickerType === 'opened' ? 'Road Opened' : 'Notification';
    Alert.alert('Sound Selected', `${soundType} sound set to: ${displayName}`);
  };

  // Sound preview handlers
  const handlePreviewSound = useCallback(async (soundIdentifier: string) => {
    // If already previewing this sound, stop it
    if (previewingSound === soundIdentifier) {
      handleStopPreview();
      return;
    }

    setPreviewLoading(true);
    setPreviewingSound(soundIdentifier);
    try {
      await previewSound(soundIdentifier);
    } catch (error: any) {
      console.warn('Could not preview sound:', error?.message || error);
    } finally {
      setPreviewLoading(false);
    }
  }, [previewingSound]);

  const handleStopPreview = useCallback(() => {
    setPreviewingSound(null);
    setPreviewLoading(false);
    stopPreview().catch(() => {});
  }, []);

  // Stop preview when sound picker modal closes
  const handleCloseSoundPicker = useCallback(() => {
    handleStopPreview();
    setShowSoundPicker(false);
    setSearchQuery('');
  }, [handleStopPreview]);

  return (
    <ScrollView style={[styles.container, nightMode && styles.containerDark, {paddingTop: insets.top}]} contentContainerStyle={{paddingBottom: Math.max(insets.bottom, 20) + 20}}>
      <View style={[styles.section, nightMode && styles.sectionDark]}>
        <Text style={[styles.sectionTitle, nightMode && styles.sectionTitleDark]}>Map Settings</Text>
        <View style={styles.settingRow}>
          <View style={styles.settingInfo}>
            <Text style={[styles.settingLabel, nightMode && styles.settingLabelDark]}>Night Mode</Text>
            <Text style={[styles.settingDescription, nightMode && styles.settingDescriptionDark]}>
              Toggle between day and night map style
            </Text>
          </View>
          <Switch
            value={nightMode}
            onValueChange={handleNightModeToggle}
            trackColor={{false: '#767577', true: '#2196F3'}}
            thumbColor={nightMode ? '#fff' : '#f4f3f4'}
          />
        </View>
      </View>

      <View style={[styles.section, nightMode && styles.sectionDark]}>
        <Text style={[styles.sectionTitle, nightMode && styles.sectionTitleDark]}>Notification Settings</Text>
        
        {/* On Duty Toggle - Municipality users only */}
        {userProfile?.role !== 'civilian' && (
          <View style={[styles.settingRow, nightMode && styles.settingRowDark]}>
            <View style={styles.settingInfo}>
              <Text style={[styles.settingLabel, nightMode && styles.settingLabelDark]}>
                {onDuty ? '🟢 On Duty' : '⚫ Off Duty'}
              </Text>
              <Text style={[styles.settingDescription, nightMode && styles.settingDescriptionDark]}>
                {onDuty 
                  ? 'You will receive notifications for road closures' 
                  : 'Notifications are paused - toggle on when ready'}
              </Text>
            </View>
            <Switch
              value={onDuty}
              onValueChange={handleOnDutyToggle}
              trackColor={{false: '#767577', true: '#4CAF50'}}
              thumbColor={onDuty ? '#fff' : '#f4f3f4'}
            />
          </View>
        )}
        
        <View style={[styles.settingRow, nightMode && styles.settingRowDark]}>
          <View style={styles.settingInfo}>
            <Text style={[styles.settingLabel, nightMode && styles.settingLabelDark]}>Road Closed Sound</Text>
            <Text style={[styles.settingDescription, nightMode && styles.settingDescriptionDark]}>
              Current: {soundClosedName === 'road_closed' ? 'Road Closed' : soundClosedName === 'system_default' ? 'System Default' : soundClosedName}
            </Text>
          </View>
        </View>

        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.button, styles.selectButton]}
            onPress={() => {
              setSoundPickerType('closed');
              setShowSoundPicker(true);
            }}>
            <Text style={styles.buttonText}>Closed Sound</Text>
          </TouchableOpacity>
        </View>

        <View style={[styles.settingRow, nightMode && styles.settingRowDark]}>
          <View style={styles.settingInfo}>
            <Text style={[styles.settingLabel, nightMode && styles.settingLabelDark]}>Road Opened Sound</Text>
            <Text style={[styles.settingDescription, nightMode && styles.settingDescriptionDark]}>
              Current: {soundOpenedName === 'road_open' ? 'Road Open' : soundOpenedName === 'system_default' ? 'System Default' : soundOpenedName}
            </Text>
          </View>
        </View>

        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.button, styles.selectButton]}
            onPress={() => {
              setSoundPickerType('opened');
              setShowSoundPicker(true);
            }}>
            <Text style={styles.buttonText}>Opened Sound</Text>
          </TouchableOpacity>
        </View>

        {Platform.OS === 'web' && (
          <Text style={{fontSize: 12, color: '#888', fontStyle: 'italic', textAlign: 'center', marginTop: 4, marginBottom: 8, paddingHorizontal: 16}}>
            Custom sounds apply to the mobile app only. Web notifications use your system default tone. Changes made here will sync to your mobile device on next login.
          </Text>
        )}

        {Platform.OS === 'web' && webNotificationPermission !== 'unsupported' && (
          <View style={styles.buttonRow}>
            {webNotificationPermission === 'default' && (
              <TouchableOpacity
                style={[styles.button, styles.selectButton]}
                onPress={handleEnableWebNotifications}>
                <Text style={styles.buttonText}>🔔 Enable Browser Notifications</Text>
              </TouchableOpacity>
            )}
            {webNotificationPermission === 'granted' && (
              <View style={[styles.button, {backgroundColor: '#2e7d32', opacity: 0.85}]}>
                <Text style={styles.buttonText}>✓ Browser Notifications Enabled</Text>
              </View>
            )}
            {webNotificationPermission === 'denied' && (
              <View style={[styles.button, {backgroundColor: '#c62828', opacity: 0.85}]}>
                <Text style={styles.buttonText}>⚠️ Notifications Blocked — Enable in browser settings</Text>
              </View>
            )}
          </View>
        )}

        {/* Sound Picker Modal */}
        <Modal
          visible={showSoundPicker}
          animationType="slide"
          transparent={true}
          supportedOrientations={['portrait', 'landscape-left', 'landscape-right']}
          onRequestClose={handleCloseSoundPicker}>
          <View style={styles.modalOverlay}>
            <View style={[styles.modalContent, nightMode && styles.modalContentDark]}>
              <View style={styles.modalHeader}>
                <Text style={[styles.modalTitle, nightMode && styles.modalTitleDark]}>
                  {soundPickerType === 'closed' 
                    ? 'Select Road Closed Sound' 
                    : soundPickerType === 'opened'
                    ? 'Select Road Opened Sound'
                    : 'Select Notification Sound'}
                </Text>
                <TouchableOpacity
                  onPress={handleCloseSoundPicker}
                  style={styles.modalCloseButton}>
                  <Text style={styles.modalCloseText}>✕</Text>
                </TouchableOpacity>
              </View>
              
              {/* Search Bar */}
              <View style={styles.searchContainer}>
                <TextInput
                  style={[styles.searchInput, nightMode && styles.searchInputDark]}
                  placeholder="Search sounds..."
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholderTextColor={nightMode ? "#999" : "#999"}
                />
              </View>

              {/* Sounds List */}
              <FlatList
                data={getFilteredSounds()}
                keyExtractor={(item) => item.filename || 'unknown'}
                contentContainerStyle={{paddingBottom: 30}}
                renderItem={({item}) => {
                  const soundIdentifier = item.filename || '';
                  let isSelected = false;
                  
                  if (soundPickerType === 'closed') {
                    isSelected = soundClosedUri === soundIdentifier;
                  } else if (soundPickerType === 'opened') {
                    isSelected = soundOpenedUri === soundIdentifier;
                  } else {
                    isSelected = soundUri === soundIdentifier;
                  }

                  const isPreviewing = previewingSound === soundIdentifier;
                  
                  return (
                    <TouchableOpacity
                      style={[
                        styles.soundItem,
                        nightMode && styles.soundItemDark,
                        isSelected && styles.soundItemSelected,
                      ]}
                      onPress={() => handleSoundSelection(soundIdentifier, item.displayName)}>
                      <View style={styles.soundItemRow}>
                        {/* Preview play/stop button */}
                        <TouchableOpacity
                          style={[
                            styles.previewButton,
                            nightMode && styles.previewButtonDark,
                            isPreviewing && styles.previewButtonActive,
                          ]}
                          onPress={(e) => {
                            e.stopPropagation();
                            handlePreviewSound(soundIdentifier);
                          }}
                          hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
                          {previewLoading && isPreviewing ? (
                            <ActivityIndicator size="small" color="#1976d2" />
                          ) : (
                            <Text style={[
                              styles.previewButtonText,
                              isPreviewing && styles.previewButtonTextActive,
                            ]}>
                              {isPreviewing ? '⏹' : '▶️'}
                            </Text>
                          )}
                        </TouchableOpacity>

                        {/* Sound name */}
                        <Text
                          style={[
                            styles.soundItemText,
                            nightMode && styles.soundItemTextDark,
                            isSelected && styles.soundItemTextSelected,
                          ]}>
                          {item.displayName}
                        </Text>
                      </View>
                      {isSelected && <Text style={styles.checkmark}>✓</Text>}
                    </TouchableOpacity>
                  );
                }}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={[styles.emptyText, nightMode && styles.emptyTextDark]}>
                      {searchQuery
                        ? 'No sounds found matching your search'
                        : 'No sounds available'}
                    </Text>
                  </View>
                }
              />
            </View>
          </View>
        </Modal>
      </View>

      {/* Mutual Aid Partners Section - Only for non-civilian users */}
      {userProfile?.role !== 'civilian' && (
      <View style={[styles.section, nightMode && styles.sectionDark]}>
        <Text style={[styles.sectionTitle, nightMode && styles.sectionTitleDark]}>Mutual Aid Partners</Text>
        <Text style={[styles.sectionDescription, nightMode && styles.settingDescriptionDark]}>
          Add neighboring cities/municipalities to view their road closures (view-only)
        </Text>

        {/* Your Mutual Aid Code */}
        <View style={[styles.settingRow, nightMode && styles.settingRowDark]}>
          <View style={styles.settingInfo}>
            <Text style={[styles.settingLabel, nightMode && styles.settingLabelDark]}>Your Mutual Aid Code</Text>
            <Text style={[styles.settingDescription, nightMode && styles.settingDescriptionDark]}>
              Share this code with other municipalities to allow them to view your closures
            </Text>
          </View>
        </View>

        <View style={[styles.codeContainer, nightMode && styles.codeContainerDark]}>
          <Text style={[styles.codeText, nightMode && styles.codeTextDark]}>{mutualAidCode || 'Loading...'}</Text>
          {mutualAidCode && (
            <TouchableOpacity
              style={styles.copyButton}
              onPress={copyCodeToClipboard}>
              <Text style={styles.copyButtonText}>Copy</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Add Partner */}
        <View style={[styles.settingRow, nightMode && styles.settingRowDark]}>
          <View style={styles.settingInfo}>
            <Text style={[styles.settingLabel, nightMode && styles.settingLabelDark]}>Add Partner</Text>
            <Text style={[styles.settingDescription, nightMode && styles.settingDescriptionDark]}>
              Enter a mutual aid code from another municipality
            </Text>
          </View>
        </View>

        <View style={styles.partnerSearchContainer}>
          <TextInput
            style={[styles.partnerSearchInput, nightMode && styles.partnerSearchInputDark]}
            placeholder="Enter mutual aid code"
            value={partnerSearchCode}
            onChangeText={setPartnerSearchCode}
            autoCapitalize="characters"
            placeholderTextColor="#999"
          />
          <TouchableOpacity
            style={[styles.addPartnerButton, searchingPartner && styles.buttonDisabled]}
            onPress={handleAddPartner}
            disabled={searchingPartner || !partnerSearchCode.trim()}>
            <Text style={styles.addPartnerButtonText}>
              {searchingPartner ? 'Searching...' : 'Add'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Partners List */}
        {partners.length > 0 && (
          <View style={styles.partnersListContainer}>
            <Text style={[styles.partnersListTitle, nightMode && styles.partnersListTitleDark]}>Active Partners ({partners.length})</Text>
            {partners.map(partner => {
              const isEnabled = account?.enabledMutualAidPartners?.includes(partner.id) ?? false;
              const notificationsEnabled = userProfile?.mutualAidNotificationPreferences?.[partner.id] ?? false;
              return (
                <View key={partner.id} style={styles.partnerItem}>
                  <View style={styles.partnerInfo}>
                    <Text style={styles.partnerName}>{partner.name}</Text>
                  </View>
                  <View style={styles.partnerTogglesRow}>
                    <View style={styles.toggleContainer}>
                      <Text style={styles.toggleLabel}>Map</Text>
                      <Switch
                        value={isEnabled}
                        onValueChange={(value) => handleTogglePartner(partner.id, value)}
                        trackColor={{false: '#767577', true: '#00aeef'}}
                        thumbColor={isEnabled ? '#fff' : '#f4f3f4'}
                      />
                    </View>
                    <View style={styles.toggleContainer}>
                      <Text style={styles.toggleLabel}>Notifications</Text>
                      <Switch
                        value={notificationsEnabled}
                        onValueChange={(value) => handleToggleMutualAidNotification(partner.id, value)}
                        trackColor={{false: '#767577', true: '#4caf50'}}
                        thumbColor={notificationsEnabled ? '#fff' : '#f4f3f4'}
                      />
                    </View>
                    <TouchableOpacity
                      style={styles.removePartnerButton}
                      onPress={() => handleRemovePartner(partner.id)}>
                      <Text style={styles.removePartnerButtonText}>Remove</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
          </View>
        )}

        {partners.length === 0 && (
          <View style={styles.emptyPartnersContainer}>
            <Text style={[styles.emptyPartnersText, nightMode && styles.emptyPartnersTextDark]}>
              No mutual aid partners added yet
            </Text>
          </View>
        )}
      </View>
      )}

      {/* Admin Section - Open All Closures */}
      {userProfile?.role === 'admin' && account?.id && (
        <View style={[styles.section, nightMode && styles.sectionDark]}>
          <Text style={[styles.sectionTitle, nightMode && styles.sectionTitleDark]}>Account Management</Text>
          <Text style={[styles.sectionDescription, nightMode && styles.settingDescriptionDark]}>
            Administrative actions for your account
          </Text>

          <TouchableOpacity
            style={[styles.openAllButton, openingAllClosures && styles.buttonDisabled]}
            onPress={handleOpenAllClosures}
            disabled={openingAllClosures}>
            <Text style={styles.openAllButtonText} numberOfLines={1} adjustsFontSizeToFit>
              {openingAllClosures ? 'Opening...' : 'Open All Closures'}
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Civilian Followed Accounts Section */}
      {userProfile?.role === 'civilian' && (
        <View style={[styles.section, nightMode && styles.sectionDark]}>
          <Text style={[styles.sectionTitle, nightMode && styles.sectionTitleDark]}>Followed Municipalities</Text>
          <Text style={[styles.sectionDescription, nightMode && styles.settingDescriptionDark]}>
            Manage the municipalities you follow and control notifications for each
          </Text>

          <TouchableOpacity
            style={styles.findButton}
            onPress={() => navigation.navigate('AccountSearch' as never)}>
            <Text style={styles.findButtonText} numberOfLines={1} adjustsFontSizeToFit>Find & Follow Municipalities</Text>
          </TouchableOpacity>

          {loadingFollowedAccounts ? (
            <View style={styles.loadingContainer}>
              <Text style={styles.loadingText}>Loading followed accounts...</Text>
            </View>
          ) : followedAccounts.length > 0 ? (
            <View style={styles.partnersListContainer}>
              <Text style={[styles.partnersListTitle, nightMode && styles.partnersListTitleDark]}>
                Following ({followedAccounts.length})
              </Text>
              {followedAccounts.map(account => {
                const notificationEnabled = userProfile.notificationPreferences?.[account.id] !== false;
                return (
                  <View key={account.id} style={styles.partnerItem}>
                    <View style={styles.partnerInfo}>
                      <Text style={styles.partnerName}>{account.name}{account.verified ? ' ✅' : ''}</Text>
                      {account.email && (
                        <Text style={styles.partnerEmail}>Email: {account.email}</Text>
                      )}
                    </View>
                    <View style={styles.partnerActions}>
                      <View style={styles.toggleContainer}>
                        <Text style={styles.toggleLabel}>Notifications</Text>
                        <Switch
                          value={notificationEnabled}
                          onValueChange={(value) => handleToggleNotification(account.id, value)}
                          trackColor={{false: '#767577', true: '#00aeef'}}
                          thumbColor={notificationEnabled ? '#fff' : '#f4f3f4'}
                        />
                      </View>
                      <TouchableOpacity
                        style={styles.removePartnerButton}
                        onPress={() => handleUnfollow(account.id)}>
                        <Text style={styles.removePartnerButtonText}>Unfollow</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </View>
          ) : (
            <View style={styles.emptyPartnersContainer}>
              <Text style={[styles.emptyPartnersText, nightMode && styles.emptyPartnersTextDark]}>
                You haven't followed any municipalities yet.{'\n'}
                Tap "Find & Follow Municipalities" to search for nearby municipalities.
              </Text>
            </View>
          )}
        </View>
      )}

      {/* Support & Information Section */}
      <View style={[styles.section, nightMode && styles.sectionDark]}>
        <Text style={[styles.sectionTitle, nightMode && styles.sectionTitleDark]}>Support & Information</Text>
        
        {/* Website Link */}
        <TouchableOpacity
          style={[styles.linkButton, nightMode && styles.linkButtonDark]}
          onPress={() => {
            Linking.openURL('https://www.respondermap.com').catch(err => {
              console.error('Failed to open website:', err);
              Alert.alert('Error', 'Could not open website. Please visit www.respondermap.com');
            });
          }}>
          <Text style={[styles.linkButtonText, nightMode && styles.linkButtonTextDark]} numberOfLines={1} adjustsFontSizeToFit>
            Visit www.respondermap.com
          </Text>
        </TouchableOpacity>

        {/* Support & Feedback Button */}
        <TouchableOpacity
          style={[styles.supportButton, nightMode && styles.supportButtonDark]}
          onPress={() => {
            const email = 'tim@respondermap.com';
            const subject = encodeURIComponent('Support & Feedback - ResponderMap');
            const body = encodeURIComponent('Hello,\n\n');
            const mailtoUrl = `mailto:${email}?subject=${subject}&body=${body}`;
            
            Linking.openURL(mailtoUrl).catch(err => {
              console.error('Failed to open email:', err);
              Alert.alert(
                'Email Not Available',
                `Please send an email to ${email} for support and feedback.`,
                [{text: 'OK'}]
              );
            });
          }}>
          <Text style={styles.supportButtonText} numberOfLines={1} adjustsFontSizeToFit>📧 Support & Feedback</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  section: {
    backgroundColor: '#fff',
    padding: 20,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    color: '#333',
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  settingInfo: {
    flex: 1,
    marginRight: 15,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 4,
  },
  settingDescription: {
    fontSize: 12,
    color: '#666',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  selectButton: {
    backgroundColor: '#2196F3',
  },
  resetButton: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  resetButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginTop: 8,
    backgroundColor: '#f9f9f9',
  },
  warningText: {
    fontSize: 12,
    color: '#ff8800',
    marginTop: 4,
    fontStyle: 'italic',
  },
  infoBox: {
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    padding: 15,
    marginTop: 10,
    marginBottom: 10,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCloseText: {
    fontSize: 20,
    color: '#666',
    fontWeight: 'bold',
  },
  soundItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  soundItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  soundItemSelected: {
    backgroundColor: '#e3f2fd',
  },
  soundItemText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  soundItemTextSelected: {
    color: '#1976d2',
    fontWeight: '600',
  },
  previewButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewButtonActive: {
    backgroundColor: '#e3f2fd',
  },
  previewButtonText: {
    fontSize: 16,
  },
  previewButtonTextActive: {
    fontSize: 14,
  },
  checkmark: {
    fontSize: 20,
    color: '#1976d2',
    fontWeight: 'bold',
  },
  searchContainer: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  searchInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#f9f9f9',
    color: '#333',
  },
  categoryContainer: {
    flexDirection: 'row',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    gap: 10,
  },
  categoryButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
  },
  categoryButtonActive: {
    backgroundColor: '#2196F3',
  },
  categoryButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  categoryButtonTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: '#666',
  },
  soundItemContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  deviceSoundBadge: {
    fontSize: 12,
    color: '#666',
    backgroundColor: '#f0f0f0',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#999',
    textAlign: 'center',
  },
  sectionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 15,
    lineHeight: 20,
  },
  codeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 15,
    marginTop: 10,
    marginBottom: 20,
  },
  codeText: {
    flex: 1,
    fontSize: 18,
    fontWeight: 'bold',
    color: '#00aeef',
    letterSpacing: 2,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  copyButton: {
    backgroundColor: '#00aeef',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  copyButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  partnerSearchContainer: {
    flexDirection: 'row',
    marginTop: 10,
    marginBottom: 20,
    gap: 10,
  },
  partnerSearchInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  addPartnerButton: {
    backgroundColor: '#00aeef',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    justifyContent: 'center',
  },
  addPartnerButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  partnersListContainer: {
    marginTop: 10,
  },
  partnersListTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  partnerItem: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
  },
  partnerInfo: {
    marginBottom: 12,
  },
  partnerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  partnerMunicipality: {
    fontSize: 14,
    color: '#666',
  },
  partnerEmail: {
    fontSize: 13,
    color: '#888',
    marginTop: 4,
  },
  partnerAccountId: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  partnerActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  partnerTogglesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
    marginTop: 8,
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  toggleLabel: {
    fontSize: 14,
    color: '#666',
  },
  removePartnerButton: {
    backgroundColor: '#d32628',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  removePartnerButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  findButton: {
    backgroundColor: '#2196F3',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    marginBottom: 20,
  },
  findButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  emptyPartnersContainer: {
    padding: 20,
    alignItems: 'center',
  },
  emptyPartnersText: {
    fontSize: 14,
    color: '#999',
    fontStyle: 'italic',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  openAllButton: {
    backgroundColor: '#f44336', // Red color for destructive action
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    marginTop: 10,
  },
  openAllButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  // Dark mode styles
  containerDark: {
    backgroundColor: '#1a1a1a', // Dark gray background
  },
  sectionDark: {
    backgroundColor: '#2a2a2a', // Darker gray for sections
  },
  sectionTitleDark: {
    color: '#fff', // White text
  },
  settingLabelDark: {
    color: '#fff', // White text
  },
  settingDescriptionDark: {
    color: '#ccc', // Light gray for descriptions
  },
  settingRowDark: {
    borderBottomColor: '#444', // Darker border
  },
  codeContainerDark: {
    backgroundColor: '#333',
  },
  codeTextDark: {
    color: '#00aeef', // Keep blue for code
  },
  partnerSearchInputDark: {
    backgroundColor: '#333',
    borderColor: '#555',
    color: '#fff',
  },
  partnersListTitleDark: {
    color: '#fff',
  },
  partnerNameDark: {
    color: '#fff',
  },
  partnerMunicipalityDark: {
    color: '#ccc',
  },
  partnerEmailDark: {
    color: '#aaa',
  },
  emptyPartnersTextDark: {
    color: '#999',
  },
  emptyTextDark: {
    color: '#999',
  },
  loadingTextDark: {
    color: '#999',
  },
  resetButtonDark: {
    backgroundColor: '#333',
    borderColor: '#555',
  },
  resetButtonTextDark: {
    color: '#fff',
  },
  searchInputDark: {
    backgroundColor: '#333',
    borderColor: '#555',
    color: '#fff',
  },
  soundItemDark: {
    backgroundColor: '#333',
    borderColor: '#555',
  },
  soundItemTextDark: {
    color: '#fff',
  },
  previewButtonDark: {
    backgroundColor: '#444',
  },
  modalContentDark: {
    backgroundColor: '#2a2a2a',
  },
  modalTitleDark: {
    color: '#fff',
  },
  modalSubtitleDark: {
    color: '#ccc',
  },
  linkButton: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  linkButtonDark: {
    backgroundColor: '#333',
    borderColor: '#555',
  },
  linkButtonText: {
    color: '#2196F3',
    fontSize: 16,
    fontWeight: '600',
    textDecorationLine: 'underline',
    flexShrink: 0,
    width: '100%',
    textAlign: 'center',
  },
  linkButtonTextDark: {
    color: '#4fc3f7',
  },
  supportButton: {
    backgroundColor: '#2196F3',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
  },
  supportButtonDark: {
    backgroundColor: '#1976d2',
  },
  supportButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    flexShrink: 0,
    width: '100%',
    textAlign: 'center',
  },
});

export default SettingsScreen;
export {SETTINGS_KEYS};

